import { supabase } from '../../../lib/supabase'

/**
 * Thin wrappers around the three_step_* multiplayer RPCs. The client
 * is dumb: every state transition lives on the server (Postgres
 * triggers + the *_maybe_advance_* helpers in 20260429010001) and
 * the UI just subscribes to changes via realtime. No client-side
 * phase math.
 */

export type ThreeStepRoomStatus =
  | 'lobby'
  | 'phase1'
  | 'phase3'
  | 'phase4_reveal'
  | 'phase4_voting'
  | 'between_rounds'
  | 'results'
  | 'abandoned'

export type ThreeStepRoomMode = 'online' | 'matchmade'

export interface ThreeStepRoomRow {
  id: string
  code: string
  host_id: string
  is_private: boolean
  mode: ThreeStepRoomMode
  status: ThreeStepRoomStatus
  current_round: number
  current_action_a: string | null
  current_action_b: string | null
  current_action_a_owner: string | null
  current_action_b_owner: string | null
  reveal_index: number
  current_turn_player_id: string | null
  turn_deadline_at: string | null
  created_at: string
  started_at: string | null
  ended_at: string | null
}

export interface ThreeStepRoomPlayerRow {
  id: string
  room_id: string
  profile_id: string | null
  is_ai: boolean
  ai_player_id: string | null
  display_name: string
  join_order: number
  total_votes: number
  /** Soft-skipped (dropped) seat: kept for prior-round votes but
   *  excluded from the count gates / current-round interaction. */
  skipped: boolean
}

export interface ThreeStepRoomActionRow {
  id: string
  room_id: string
  player_id: string
  slot: 0 | 1 | 2
  text: string
  is_used: boolean
  /** Which round consumed this action and as which prompt slot.
   *  Null on rooms finished before 20260815120000 stamped history. */
  used_in_round: number | null
  used_as_slot: 'a' | 'b' | null
  created_at: string
}

export interface ThreeStepRoundStepsRow {
  id: string
  room_id: string
  player_id: string
  round: number
  step1_text: string
  step2_text: string
  step3_text: string
  votes: number
  submitted_at: string | null
}

export interface ThreeStepRoundVoteRow {
  id: string
  room_id: string
  round: number
  voter_id: string
  target_id: string
  created_at: string
}

export interface ThreeStepAiPersonaRow {
  id: string
  name: string
  persona: string
  avatar_emoji: string
  model: string
  is_active: boolean
}

// ── Mutations ─────────────────────────────────────────────────────────

// All RPCs cast through `as any` because database.types.ts hasn't been
// regen'd yet for the new functions. The runtime is fine; this is just
// to keep TypeScript from refusing to compile.

/* eslint-disable @typescript-eslint/no-explicit-any */

export async function createThreeStepRoom(isPrivate = false): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('create_three_step_room', {
    p_is_private: isPrivate,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function joinThreeStepRoomByCode(code: string): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('join_three_step_room_by_code', {
    p_code: code.toUpperCase(),
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

/**
 * TV-side lookup — find a room by code without joining as a player.
 * The TV display is read-only; we just need the room id to subscribe.
 * RLS allows any authed user (including anon TV sessions) to SELECT
 * three_step_rooms, so this is just a regular query.
 */
export async function fetchThreeStepRoomByCode(code: string): Promise<ThreeStepRoomRow | null> {
  const { data, error } = await (supabase as any)
    .from('three_step_rooms').select('*').eq('code', code.toUpperCase()).maybeSingle()
  if (error) throw error
  return (data as ThreeStepRoomRow | null) ?? null
}

export async function leaveThreeStepRoom(roomId: string): Promise<void> {
  const { error } = await (supabase as any).rpc('leave_three_step_room', { p_room_id: roomId })
  if (error) throw error
}

export async function addThreeStepAi(
  roomId: string, aiPlayerId: string,
): Promise<ThreeStepRoomPlayerRow> {
  const { data, error } = await (supabase as any).rpc('add_three_step_ai_player', {
    p_room_id: roomId, p_ai_player_id: aiPlayerId,
  })
  if (error) throw error
  return data as ThreeStepRoomPlayerRow
}

export async function removeThreeStepAi(playerId: string): Promise<void> {
  const { error } = await (supabase as any).rpc('remove_three_step_ai_player', {
    p_player_id: playerId,
  })
  if (error) throw error
}

/**
 * Rename your seat in the lobby. Updates both the seat snapshot and
 * profile.display_name in one RPC, mirroring rename_iet_seat /
 * rename_colorful_seat. Used by the in-room "Your name" field so a
 * guest who landed as "Player" can fix it without leaving + rejoining.
 */
export async function renameThreeStepSeat(
  roomId: string, name: string,
): Promise<void> {
  const { error } = await (supabase as any).rpc('rename_three_step_seat', {
    p_room_id: roomId, p_name: name,
  })
  if (error) throw error
}

export async function startThreeStepGame(roomId: string): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('start_three_step_game', {
    p_room_id: roomId,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function submitThreeStepActions(
  roomId: string, a: string, b: string, c: string,
): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('submit_three_step_actions', {
    p_room_id: roomId, p_a: a, p_b: b, p_c: c,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function submitThreeStepActionsAsAi(
  playerId: string, a: string, b: string, c: string,
): Promise<void> {
  const { error } = await (supabase as any).rpc('submit_three_step_actions_as_ai', {
    p_player_id: playerId, p_a: a, p_b: b, p_c: c,
  })
  if (error) throw error
}

export async function submitThreeStepRoundSteps(
  roomId: string, s1: string, s2: string, s3: string,
): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('submit_three_step_round_steps', {
    p_room_id: roomId, p_step1: s1, p_step2: s2, p_step3: s3,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function submitThreeStepRoundStepsAsAi(
  playerId: string, s1: string, s2: string, s3: string,
): Promise<void> {
  const { error } = await (supabase as any).rpc('submit_three_step_round_steps_as_ai', {
    p_player_id: playerId, p_step1: s1, p_step2: s2, p_step3: s3,
  })
  if (error) throw error
}

export async function stepBackThreeStepReveal(
  roomId: string, expectedIndex?: number,
): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('step_back_three_step_reveal', {
    p_room_id: roomId, p_expected_index: expectedIndex ?? null,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function advanceThreeStepReveal(
  roomId: string, expectedIndex?: number,
): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('advance_three_step_reveal', {
    p_room_id: roomId, p_expected_index: expectedIndex ?? null,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function castThreeStepRoundVote(
  roomId: string, targetPlayerId: string,
): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('cast_three_step_round_vote', {
    p_room_id: roomId, p_target_player_id: targetPlayerId,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function castThreeStepRoundVoteAsAi(
  playerId: string, targetPlayerId: string,
): Promise<void> {
  const { error } = await (supabase as any).rpc('cast_three_step_round_vote_as_ai', {
    p_player_id: playerId, p_target_player_id: targetPlayerId,
  })
  if (error) throw error
}

export async function advanceThreeStepRound(
  roomId: string, expectedRound?: number,
): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('advance_three_step_round', {
    p_room_id: roomId, p_expected_round: expectedRound ?? null,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

/**
 * Soft-skips a dropped seat so the count-gated phase advance can fire.
 * Any player in the room can call it (the host may be the one who
 * dropped). The seat's rows are kept — votes won in earlier rounds
 * survive — it's just excluded from the gates. See migration
 * 20260627000000_three_step_host_independence_and_soft_skip.sql.
 */
export async function kickThreeStepPlayer(playerId: string): Promise<ThreeStepRoomRow> {
  const { data, error } = await (supabase as any).rpc('kick_three_step_player', {
    p_player_id: playerId,
  })
  if (error) throw error
  return data as ThreeStepRoomRow
}

export async function sweepThreeStepMatchmadeIdle(): Promise<number> {
  const { data, error } = await (supabase as any).rpc('sweep_three_step_matchmade_idle', {})
  if (error) throw error
  return Number(data ?? 0)
}

// ── Reads ─────────────────────────────────────────────────────────────

export async function fetchThreeStepRoom(roomId: string): Promise<ThreeStepRoomRow | null> {
  const { data, error } = await (supabase as any)
    .from('three_step_rooms').select('*').eq('id', roomId).maybeSingle()
  if (error) throw error
  return (data as ThreeStepRoomRow | null) ?? null
}

export async function fetchThreeStepPlayers(roomId: string): Promise<ThreeStepRoomPlayerRow[]> {
  const { data, error } = await (supabase as any)
    .from('three_step_room_players').select('*').eq('room_id', roomId).order('join_order')
  if (error) throw error
  return (data ?? []) as ThreeStepRoomPlayerRow[]
}

export async function fetchThreeStepActions(roomId: string): Promise<ThreeStepRoomActionRow[]> {
  const { data, error } = await (supabase as any)
    .from('three_step_room_actions').select('*').eq('room_id', roomId).order('created_at')
  if (error) throw error
  return (data ?? []) as ThreeStepRoomActionRow[]
}

export async function fetchThreeStepRoundSteps(
  roomId: string, round?: number,
): Promise<ThreeStepRoundStepsRow[]> {
  let q = (supabase as any).from('three_step_round_steps').select('*').eq('room_id', roomId)
  if (typeof round === 'number') q = q.eq('round', round)
  const { data, error } = await q.order('submitted_at', { ascending: true, nullsFirst: false })
  if (error) throw error
  return (data ?? []) as ThreeStepRoundStepsRow[]
}

export async function fetchThreeStepRoundVotes(
  roomId: string, round?: number,
): Promise<ThreeStepRoundVoteRow[]> {
  let q = (supabase as any).from('three_step_round_votes').select('*').eq('room_id', roomId)
  if (typeof round === 'number') q = q.eq('round', round)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as ThreeStepRoundVoteRow[]
}

export async function fetchThreeStepAiPersonas(): Promise<ThreeStepAiPersonaRow[]> {
  const { data, error } = await (supabase as any)
    .from('three_step_ai_players').select('*').eq('is_active', true).order('name')
  if (error) throw error
  return (data ?? []) as ThreeStepAiPersonaRow[]
}

// ── Snapshot + realtime subscription ──────────────────────────────────

export interface ThreeStepRoomSnapshot {
  room: ThreeStepRoomRow
  players: ThreeStepRoomPlayerRow[]
  actions: ThreeStepRoomActionRow[]
  steps: ThreeStepRoundStepsRow[]
  votes: ThreeStepRoundVoteRow[]
  /** Per-phase player_id sets sourced via SECURITY DEFINER RPC so the
   *  TV (which can't SELECT actions/steps under RLS during phase1/3)
   *  still gets accurate done-counts. Same data the TV uses for the
   *  "X / N done" headlines. */
  progress: {
    phase1DoneIds: string[]
    phase3DoneIds: string[]
    phase4VotedIds: string[]
  }
}

async function fetchThreeStepRoomProgress(roomId: string) {
  const { data, error } = await (supabase as any).rpc('three_step_room_progress', {
    p_room_id: roomId,
  })
  if (error) throw error
  // RETURNS TABLE → array of rows; the function returns exactly one row.
  const row = Array.isArray(data) ? data[0] : data
  return {
    phase1DoneIds:  (row?.phase1_done_player_ids  ?? []) as string[],
    phase3DoneIds:  (row?.phase3_done_player_ids  ?? []) as string[],
    phase4VotedIds: (row?.phase4_voted_player_ids ?? []) as string[],
  }
}

export async function fetchThreeStepRoomSnapshot(roomId: string): Promise<ThreeStepRoomSnapshot> {
  const room = await fetchThreeStepRoom(roomId)
  if (!room) throw new Error('Room not found')
  const [players, actions, steps, votes, progress] = await Promise.all([
    fetchThreeStepPlayers(roomId),
    fetchThreeStepActions(roomId),
    fetchThreeStepRoundSteps(roomId),
    fetchThreeStepRoundVotes(roomId),
    fetchThreeStepRoomProgress(roomId),
  ])
  return { room, players, actions, steps, votes, progress }
}

/**
 * Subscribe to all five tables for one room, calling `onChange` after
 * any insert/update/delete. Caller refetches the snapshot — much
 * simpler than splice-merging individual rows.
 */
export function subscribeToThreeStepRoom(
  roomId: string,
  onChange: () => void,
): () => void {
  const channel = (supabase as any)
    .channel(`three_step_room_${roomId}`)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'three_step_rooms', filter: `id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'three_step_room_players', filter: `room_id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'three_step_room_actions', filter: `room_id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'three_step_round_steps', filter: `room_id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'three_step_round_votes', filter: `room_id=eq.${roomId}` },
        onChange)
    .subscribe()

  return () => { (supabase as any).removeChannel(channel) }
}

// ── Online matchmade queue ────────────────────────────────────────────

export async function fetchMyMatchmadeThreeStepRoom(): Promise<ThreeStepRoomRow | null> {
  const { data, error } = await (supabase as any).rpc('my_matchmade_three_step_room', {})
  if (error) throw error
  const row = data as ThreeStepRoomRow | null
  if (!row || !row.id) return null
  return row
}

// ── AI generation via Edge Function (Claude Haiku 4.5) ───────────────

/**
 * Ask the `three-step-ai-turn` Edge Function to generate three lines
 * (actions or steps) for an AI seat. Returns null on any failure —
 * caller falls back to the local persona templates so the game never
 * stalls because of an LLM hiccup.
 */
async function callThreeStepAiEdge(payload: {
  kind: 'actions' | 'steps'
  player_id: string
  action_a?: string
  action_b?: string
}): Promise<[string, string, string] | null> {
  try {
    const { data, error } = await supabase.functions.invoke('three-step-ai-turn', {
      body: payload,
    })
    if (error) {
      console.warn('[3step ai-edge] invoke error', error)
      return null
    }
    const items = (data as { items?: unknown[] } | null)?.items
    if (!Array.isArray(items) || items.length < 3) return null
    return [String(items[0] ?? ''), String(items[1] ?? ''), String(items[2] ?? '')]
  } catch (e) {
    console.warn('[3step ai-edge] threw', e)
    return null
  }
}

export async function generateAiActions(
  playerId: string,
): Promise<[string, string, string] | null> {
  return callThreeStepAiEdge({ kind: 'actions', player_id: playerId })
}

export async function generateAiSteps(
  playerId: string, actionA: string, actionB: string,
): Promise<[string, string, string] | null> {
  return callThreeStepAiEdge({
    kind: 'steps', player_id: playerId, action_a: actionA, action_b: actionB,
  })
}
